import { useEffect, useId, useRef, type JSX } from 'react';
import type { ItemOrcamento } from '../estado/gerador.js';
import { moedaBr, rotuloTabela } from '../lib/formato.js';
import './DuplicarComo.css';
import './ConfirmarTabela.css';

/**
 * "Confira a tabela antes de salvar" — S12, CA-6.
 *
 * Abre ao emitir o cenário do ORC-2026-0053: Engenharia, RT zero, e rodapé ou
 * vinílico no pedido. Foi como a revenda da tabela RT 5 saiu pelo preço de
 * engenharia, R$ 85 abaixo do pedido Villagres.
 *
 * **Não bloqueia.** Venda de engenharia sem RT existe, e o vendedor continua
 * podendo emitir. O aviso só obriga a uma escolha consciente.
 *
 * O preço na tabela RT 5 / RT 10 NÃO aparece: o front não tem esse número sem
 * uma chamada nova (ou um endpoint), e a spec manda omitir só ele.
 *
 * O foco abre no **Voltar**, a opção segura: emitir é irreversível, e um Enter
 * por reflexo não pode virar emissão. Esc também volta.
 */
interface Props {
  /** Um item rodapé/vinílico do orçamento — o exemplo do texto. */
  exemplo: Pick<ItemOrcamento, 'referencia' | 'baseAvista' | 'unidade' | 'precoManual'>;
  aoDuplicar: () => void;
  aoContinuar: () => void;
  aoVoltar: () => void;
}

const TITULO = 'Confira a tabela antes de salvar';

export function ConfirmarTabela({
  exemplo,
  aoDuplicar,
  aoContinuar,
  aoVoltar,
}: Props): JSX.Element {
  const porUnidade = exemplo.unidade === 'metro_linear' ? 'm' : 'm²';
  const tabelaComRt = rotuloTabela('RT10');
  const idTexto = useId();
  const voltar = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    voltar.current?.focus();
  }, []);

  return (
    <div
      className="dialogo"
      role="dialog"
      aria-modal="true"
      aria-label={TITULO}
      aria-describedby={idTexto}
      onKeyDown={(e) => {
        if (e.key === 'Escape') aoVoltar();
      }}
    >
      <div className="dialogo__caixa dialogo__caixa--tabela">
        <h2 className="dialogo__titulo">{TITULO}</h2>
        <p className="dialogo__texto" id={idTexto}>
          Este orçamento está em {rotuloTabela('CPF')} com RT 0: preço de engenharia,{' '}
          <strong>SEM RT</strong>
          {/* Preço digitado à mão não é "preço de engenharia": sem exemplo. */}
          {exemplo.precoManual
            ? ''
            : ` (ex.: ${exemplo.referencia} = R$ ${moedaBr(exemplo.baseAvista)}/${porUnidade})`}
          . Se é revenda com o RT já embutido pela Villagres, use {tabelaComRt}.
        </p>
        <div className="dialogo__acoes">
          <button ref={voltar} type="button" className="botao botao--neutro" onClick={aoVoltar}>
            Voltar
          </button>
          <button type="button" className="botao botao--neutro" onClick={aoContinuar}>
            Continuar sem RT
          </button>
          <button type="button" className="botao botao--primario" onClick={aoDuplicar}>
            Duplicar em {tabelaComRt}
          </button>
        </div>
      </div>
    </div>
  );
}
